import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import { GetAnalysisByIdQueryVariables, GetUserAnalysesPaginatedQuery } from '@/shared/api/types/generated';
import { GetPublicAnalysisCategoriesQuery, useGetAnalysisByIdQuery } from '@/shared/hooks/queries/generated';

export const useGetAnalysisByIdWithCache: typeof useGetAnalysisByIdQuery = ((
  variables: GetAnalysisByIdQueryVariables,
  options: any = {},
) => {
  const queryClient = useQueryClient();

  const getPlaceholderData = () => {
    // Pour les infinite queries, les données sont stockées dans une structure différente
    const userAnalysesCatalogDataResults = queryClient.getQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>({
      queryKey: ['GetUserAnalysesPaginated', 'catalog'],
    });
    const userAnalysesProfileDataResults = queryClient.getQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>({
      queryKey: ['GetUserAnalysesPaginated', 'profile'],
    });
    const publicAnalysesDataResults = queryClient.getQueriesData<GetPublicAnalysisCategoriesQuery>({
      queryKey: ['GetPublicAnalysisCategories'],
    });

    // Extraire les analyses depuis les infinite queries
    const allCachedAnalysis = userAnalysesCatalogDataResults
      .map((x) => {
        // x[1] est l'InfiniteData avec les pages
        const infiniteData = x[1];
        return infiniteData?.pages?.flatMap((page) => page.getUserAnalysesPaginated?.analyses ?? []) ?? [];
      })
      .flat()
      .concat(
        userAnalysesProfileDataResults
          .map((x) => {
            const infiniteData = x[1];
            return infiniteData?.pages?.flatMap((page) => page.getUserAnalysesPaginated?.analyses ?? []) ?? [];
          })
          .flat(),
        publicAnalysesDataResults
          .map((x) => x[1]?.getPublicAnalysisCategories?.map((y) => y.analyses).flat() ?? [])
          .flat(),
      );

    const cachedAnalysis = allCachedAnalysis.find((analysis) => analysis.id === variables.id);
    if (cachedAnalysis) {
      return {
        getAnalysisById: cachedAnalysis,
      };
    }

    return undefined;
  };

  return useGetAnalysisByIdQuery(variables, {
    placeholderData: getPlaceholderData,
    ...options,
  });
}) as any;
